import HTMLTemplate from './template.html'
import './styles.scss'
import ClientPortalAPIService from './../../../../services/api/ClientPortal/ApiService'
import Papa from 'papaparse'
import { debounce } from 'lodash'
import phoneCodes from './../../../../data/phone-codes.json'
import { isMobilePhone, isEmpty } from 'validator'
import { CrewManagement } from './../CrewManagement/CrewManagement'
import { orderBy } from 'lodash'
import tippy, { followCursor } from 'tippy.js'
import Sentry from './../../../../modules/SantryIO'
import User from '../../../../modules/user'
import { Sprite } from './../../components/Sprite/Sprite'

const uniqueCountryCodes = []
phoneCodes.forEach(codeDetails => {
    if (uniqueCountryCodes.indexOf(codeDetails.PhoneCode) === -1) uniqueCountryCodes.push(codeDetails.PhoneCode)
})
uniqueCountryCodes.sort((a, b) => {
    const asIntegerA = parseInt(a, 10)
    const asIntegerB = parseInt(b, 10)

    if (asIntegerA < asIntegerB) return -1
    if (asIntegerA > asIntegerB) return 1
    return 0
})

export const OrganisationSettingsModal = (() => {
    const vueComponent = {
        data() {
            return {
                showSettings: false,
                activeSettingsSection: 'General',
                singleTailNumberToAdd: '',
                isUploadingTails: false,
                numberOfTailsToUpload: 0,
                isDeletingTails: false,
                tailErrorMessage: '',
                tailsFileRef: null,
                tailsFileName: 'Upload File',
                organisationTails: [],
                infoTitle: '',
                infoMessage: '',
                showDeletingTailConfirmation: false,
                tailIDtoRemove: null,
                showCSVUploadResults: false,
                filedCSVtails: [],
                alertSeverities: [],
                isSavingClientDetails: false,

                // General settings
                phoneCodes: uniqueCountryCodes,
                selectedPhoneCodePrefix: '+1',
                emergencyNumber: '',
                emergencyNumberError: '',

                // Related to crew
                isLoading: false,
                isLoadingClientCrew: false,
                clientCrew: [],
                openCrewDropdown: false,
                crewIDToRemove: null,
                clientName: '',

                // Critical contact
                criticalContacts: [],
                critcalContactName: '',
                selectedCriticalContactPhoneCodePrefix: '+1',
                criticalContactNumber: '',
                isAddingCriticalContact: false,
                criticalContactToDelete: null,
                contactNameError: '',
                phoneNumberError: '',
                contactAPIError: '',
                isCrewChechkAutomation: false,
                showAddTailSuccessModal: false
            }
        },
        created() {
            User.getUserPermissions().then(permissions => {
                if (permissions?.includes('crew_check_automation')) {
                    this.isCrewChechkAutomation = true
                }
            })
        },
        methods: {
            reorderAlphabetically(listToReorder) {
                let reorderedList = listToReorder
                reorderedList = reorderedList.sort((a, b) => a.name.localeCompare(b.name))
                return reorderedList
            },
            show(sectionToOpen) {
                if (sectionToOpen !== undefined) this.activeSettingsSection = sectionToOpen

                this.showSettings = true
                ClientPortalAPIService.getOrganisationTails().then(tails => this.organisationTails = tails)

                if (!this.isCrewChechkAutomation) {
                    this.isLoadingClientCrew = true
                    ClientPortalAPIService.getClientCrew()
                        .then(response => this.clientCrew = response.sort((a, b) => a.name.localeCompare(b.name)))
                        .finally(() => this.isLoadingClientCrew = false)
                }

                // Get and then reorder critical contacts alphabetically based on name
                ClientPortalAPIService.getCriticalContacts().then(criticalContacts => this.criticalContacts = criticalContacts.sort((a, b) => a.name.localeCompare(b.name)))

                // Reset some states
                this.contactNameError = ''
                this.phoneNumberError = ''
                this.contactAPIError = ''
            },
            addTail() {
                const tailNumber = this.singleTailNumberToAdd.trim()
                if (tailNumber === '') {
                    this.tailErrorMessage = 'Tail number is a required field'
                } else if (tailNumber.includes('/')) {
                    this.tailErrorMessage = 'Tail Numbers with “/” are not accepted. Please update and try again.'
                } else if (tailNumber.includes('\\')) {
                    this.tailErrorMessage = 'Tail Numbers with “\\” are not accepted. Please update and try again.'
                } else {
                    this.isUploadingTails = true
                    ClientPortalAPIService.uploadOrganisationTails({ tails: tailNumber }).then(tailResponse => {
                        if (tailResponse[0].success) {
                            this.showAddTailSuccessModal = true
                            ClientPortalAPIService.getOrganisationTails().then(tails => {
                                this.organisationTails = tails
                                this.singleTailNumberToAdd = ''
                                this.displayInfoMessage('Tail added', 'Aircraft tracking and info will be available during the aircraft\'s next flight.')
                            }).finally(() => this.isUploadingTails = false)
                        } else {
                            this.displayInfoMessage('Tail error', 'We were not able to add this tail to your fleet.')
                            this.isUploadingTails = false
                        }
                    })
                        .catch(error => {
                            Sentry.captureException(error)
                            this.tailErrorMessage = error
                            this.isUploadingTails = false
                        })
                }
            },
            addTails() {
                const uploadedFile = this.$refs.tailsFile.files[0]
                const reader = new FileReader()

                // It's a little tricky to do front-end valdation of CSV files, I think we will make few assumptions. For more info:
                // About MIME type validation: https://stackoverflow.com/a/42140178/2052173
                const allowedFileTypes = ['text/plain', 'application/vnd.ms-excel', 'text/x-csv', 'application/csv', 'application/x-csv', 'text/csv', 'text/comma-separated-values', 'text/x-comma-separated-values', 'text/tab-separated-values']

                if (uploadedFile === undefined) {
                    this.tailErrorMessage = 'File input is a required field'
                    return false
                }

                if (allowedFileTypes.indexOf(this.$refs.tailsFile.files[0].type) === -1) {
                    this.tailErrorMessage = 'You have uploaded an unsupported filetype. Please ensure you are uploading a .CSV file with standard formatting.'
                    return false
                }

                this.filedCSVtails = []

                reader.onload = (event) => {
                    const papaParseResults = Papa.parse(event.target.result)

                    // It's hard to decide what error we should handle, we ask for a simple list, so in this case the library will thoww:
                    // "Unable to auto-detect delimiting character; defaulted to ','", which we don't care, as we ask for just a simple list
                    // Leave unhandled as for now and handle papaParseResults.errors if it will reveal to be necessary 
                    console.log('Result of the parsef file from Papaparse', papaParseResults)

                    if (!!papaParseResults.data) {
                        let tailNumbersToUpload = []
                        papaParseResults.data.forEach(CSVLine => {
                            // Assumption is that a tail number is first element on the row, we don't care about the rest
                            if (CSVLine[0].trim() !== '') {
                                if (CSVLine[0].includes('/') || CSVLine[0].includes('\\')) {
                                    this.filedCSVtails.push(CSVLine[0]) 
                                } else {
                                    tailNumbersToUpload.push(CSVLine[0])
                                }
                            } 
                        })

                        // filedCSVtails.push('dddd')

                        if (tailNumbersToUpload.length === 0) {
                            this.tailErrorMessage = 'Could not read any tail numbers from the file'
                            return false
                        }

                        this.numberOfTailsToUpload = tailNumbersToUpload.length
                        let commaSeparatedTailNumbers = tailNumbersToUpload.join(',')
                        this.isUploadingTails = true

                        ClientPortalAPIService.uploadOrganisationTails({ tails: commaSeparatedTailNumbers }).then(responseTails => {
                            // let failedTailsToProcess = []
                            responseTails.forEach(tailResponse => {
                                if (tailResponse.tailNumber !== '' && !tailResponse.success) {
                                    this.filedCSVtails.push(tailResponse.tailNumber)
                                }
                            })

                            if (this.filedCSVtails.length > 0) {
                                this.showCSVUploadResults = true
                                // this.filedCSVtails = failedTailsToProcess
                            } else {
                                this.displayInfoMessage('Tails added', 'Aircraft tracking and info will be available during the aircraft\'s next flight.')
                            }

                            ClientPortalAPIService.getOrganisationTails().then(tails => {
                                this.organisationTails = tails
                            }).finally(() => {
                                this.isUploadingTails = false
                                this.tailsFileName = 'Upload File'
                                this.$refs.tailsFile.value = ''
                            })
                        })
                            .catch(error => {
                                Sentry.captureException(error)
                                this.tailErrorMessage = error
                                this.isUploadingTails = false
                            })
                    }
                }

                reader.readAsText(uploadedFile)
            },
            displayInfoMessage(title, message) {
                this.infoTitle = title
                this.infoMessage = message
            },
            hideInfoMessage() {
                this.infoTitle = ''
                this.infoMessage = ''
            },
            startDeleteTail(tailID) {
                this.showDeletingTailConfirmation = true
                this.tailIDtoRemove = tailID
            },
            stopDeletingTail() {
                this.showDeletingTailConfirmation = false
                this.tailIDtoRemove = null
            },
            deleteTail(tailID) {
                this.isDeletingTails = true
                ClientPortalAPIService.deleteOrganisationTail(tailID).then(() => {
                    ClientPortalAPIService.getOrganisationTails().then(tails => {
                        this.organisationTails = tails
                        this.isDeletingTails = false

                        this.stopDeletingTail()
                    })
                })
            },
            onFileUpload($event) {
                if ($event.target.files.length === 0) {
                    this.tailsFileName = 'Upload File'
                } else {
                    this.tailsFileName = $event.target.files[0].name
                }
            },
            saveClientDetails() {
                if (this.isSavingClientDetails) return false

                this.isSavingClientDetails = true
                ClientPortalAPIService.getClientDetails().then(clientDetails => {
                    let updatedSettings = clientDetails.settings
                    updatedSettings.severity = this.alertSeverities

                    this.emergencyNumberError = ''
                    this.emergencyNumber = this.emergencyNumber.trim()
                    if (isEmpty(this.emergencyNumber)) {
                        updatedSettings.clientEmergencyNumber = ''
                    } else if (this.emergencyNumber.length < 7) {
                        this.emergencyNumberError = 'The entered phone number is too short.'
                    } else if (this.emergencyNumber.length > 7) {
                        const completePhoneNumber = this.selectedPhoneCodePrefix + ' ' + this.emergencyNumber
                        const completePhoneNumberNoSpaces = (this.selectedPhoneCodePrefix + this.emergencyNumber).replaceAll(' ', '')
                        const isMobilePhoneNumber = isMobilePhone(completePhoneNumberNoSpaces, 'any', { strictMode: true })
                        if (isMobilePhoneNumber) {
                            updatedSettings.clientEmergencyNumber = completePhoneNumber
                        } else {
                            let errorMessage = 'You have entered an invalid phone number, please check and try again.'
                            this.emergencyNumberError = errorMessage
                        }
                    }

                    ClientPortalAPIService.updateClientSettings({ settings: JSON.stringify(updatedSettings) })
                })
                    .finally(() => {
                        // Fake delay, as the API is just to fast and we need user to have some visual indication in the UI
                        window.setTimeout(() => this.isSavingClientDetails = false, 500)
                    })
            },
            debounceSavingClientDetails: debounce(function () {
                this.saveClientDetails()
            }, 1500),
            toggleAlertSeverity(severityName) {
                const indexOfSeverity = this.alertSeverities.indexOf(severityName)
                if (indexOfSeverity > -1) {
                    this.alertSeverities.splice(indexOfSeverity, 1)
                } else {
                    this.alertSeverities.push(severityName)
                }

                this.debounceSavingClientDetails()
            },
            showAddCrewModal(preventClearingFormData) {
                this.openCrewDropdown = false
                CrewManagement.showAddCrewModal(preventClearingFormData).then(crewData => {
                    ClientPortalAPIService.addCrew([crewData]).then(response => {
                        // If there was an error adding the user (most probably a duplicated phone or email), then we show a message
                        // to the user already exists to use the the Look-up functionality
                        if (response.length === 0) {
                            CrewManagement.communicateCrewSubmissionServerError('User with the specified phone or email address already exists.')
                            this.showAddCrewModal(true)
                        } else {
                            this.clientCrew.unshift(response[0])
                            this.reorderAlphabetically(this.clientCrew)

                            CrewManagement.communicateCrewSubmission()
                        }
                    })
                }).catch(error => Sentry.captureException(error))
            },
            showEditCrewModal(crewDetails, preventFormUpdate) {
                CrewManagement.showEditCrewModal(crewDetails, preventFormUpdate).then(updatedCrewData => {
                    ClientPortalAPIService.updateCrew(updatedCrewData).then(() => {
                        let indexExistingCrewToEdit = this.clientCrew.findIndex(crew => crew.id === updatedCrewData.Id)
                        this.clientCrew[indexExistingCrewToEdit] = {
                            ...this.clientCrew[indexExistingCrewToEdit],
                            name: updatedCrewData.Name,
                            email: updatedCrewData.Email,
                            phone: updatedCrewData.Phone,
                            role: updatedCrewData.Role
                        }

                        this.reorderAlphabetically(this.clientCrew)

                        CrewManagement.communicateCrewSubmission()
                    }).catch(error => {
                        Sentry.captureException(error)
                        CrewManagement.communicateCrewSubmissionServerError(error.response.data)
                        this.showEditCrewModal(crewDetails, true)
                    })
                }).catch(error => Sentry.captureException(error))
            },
            showAddBulkCrewModal() {
                this.openCrewDropdown = false
                CrewManagement.showBulkAddCrewModal().then(crewDetailsToAdd => {
                    ClientPortalAPIService.addCrew(crewDetailsToAdd).then(addedCrew => {
                        // So the addCrew API works in a way that it will return in the response the users that were actually added,
                        // if for any reasons it failed to add it (duplicates, errors), then it will not return that crew in the response
                        // so FE side can figure out failed users by checking which were uploaded and which were not returned in response
                        let bulkUploadedUsers = addedCrew
                        let bulkFailedUsers = []

                        addedCrew.forEach(crew => {
                            this.clientCrew.unshift(crew)
                        })

                        // Find not added users 
                        crewDetailsToAdd.forEach(crewToAdd => {
                            let hasAddedUser = addedCrew.findIndex(crew => crewToAdd.Phone === crew.phone) > -1
                            if (!hasAddedUser) {
                                bulkFailedUsers.push(crewToAdd)
                            }
                        })

                        CrewManagement.communicateCrewSubmission(bulkUploadedUsers, bulkFailedUsers)
                    })
                }).catch(error => Sentry.captureException(error))
            },
            showDeleteCrewConfirmation(crewID) {
                this.crewIDToRemove = crewID
            },
            deleteCrew(crewID) {
                this.isLoading = true
                ClientPortalAPIService.deleteCrew(crewID).then(() => {
                    this.clientCrew = this.clientCrew.filter(crew => crew.id !== crewID)
                    this.crewIDToRemove = null
                }).finally(() => this.isLoading = false)
            },
            addCriticalContact() {
                this.isAddingCriticalContact = true
                this.contactNameError = ''
                this.phoneNumberError = ''
                this.contactAPIError = ''

                this.critcalContactName = this.critcalContactName.trim()
                if (isEmpty(this.critcalContactName)) {
                    this.contactNameError = 'Contact name is required'
                    this.isAddingCriticalContact = false
                    return false
                } else if (isEmpty(this.criticalContactNumber)) {
                    this.phoneNumberError = 'Contact number is required'
                    this.isAddingCriticalContact = false
                    return false
                } else if (this.criticalContactNumber.length < 7) {
                    this.phoneNumberError = 'Contact number is too short'
                    this.isAddingCriticalContact = false
                    return false
                } else if (this.criticalContactNumber.length > 7) {
                    const completePhoneNumber = this.selectedCriticalContactPhoneCodePrefix + ' ' + this.criticalContactNumber
                    const completePhoneNumberNoSpaces = (this.selectedCriticalContactPhoneCodePrefix + this.criticalContactNumber).replaceAll(' ', '')
                    const isMobilePhoneNumber = isMobilePhone(completePhoneNumberNoSpaces, 'any', { strictMode: true })
                    if (!isMobilePhoneNumber) {
                        this.phoneNumberError = 'You have entered an invalid phone number, please check and try again.'
                        this.isAddingCriticalContact = false
                        return false
                    }

                    ClientPortalAPIService.addCriticalContact({
                        Name: this.critcalContactName,
                        Number: completePhoneNumber
                    }).then(response => {
                        this.criticalContacts.unshift(response)
                        // Reorder critical contacts alphabetically based on name after added
                        this.criticalContacts = this.criticalContacts.sort((a, b) => a.name.localeCompare(b.name))
                        this.critcalContactName = ''
                        this.criticalContactNumber = ''
                    }).catch(error => {
                        Sentry.captureException(error)
                        this.contactAPIError = error.response.data
                    }).finally(() => {
                        this.isAddingCriticalContact = false
                    })
                }
            },
            showCriticalContactDeleteConfirmation(criticalContactToDelete) {
                this.criticalContactToDelete = criticalContactToDelete
            },
            deleteCriticalContact(criticalContactToDelete) {
                this.isLoading = true
                ClientPortalAPIService.deleteCriticalContact(criticalContactToDelete).then(() => {
                    this.criticalContacts = this.criticalContacts.filter(contact => contact.id !== criticalContactToDelete)
                    this.criticalContactToDelete = null
                }).finally(() => this.isLoading = false)
            },
            sortArray(array, property) {
                return orderBy(array, property, 'asc')
            }
        },
        template: HTMLTemplate
    }

    const DOMElement = document.getElementById('vue-organisation-settings-modal')
    if (DOMElement === null) {
        return null
    } else {
        return Vue.createApp(vueComponent).use({
            install: (app) => {
                app.directive('tooltip', {
                    beforeMount(el, binding) {
                        tippy(el, {
                            content: binding.value,
                            placement: 'bottom-start',
                            animation: 'shift-away',
                            allowHTML: true,
                            trigger: 'mouseenter click focus',
                            arrow: false,
                            followCursor: true,
                            plugins: [followCursor]
                        })
                    }
                })
            }
        }).mount(DOMElement)
    }
})();